// src/components/ProductList/components/ProductItem.js
import React from "react";
import { useTranslation } from "react-i18next";
import {
  getLocalizedProductName,
  getProductImage,
} from "../utils/productListUtils";

const getConfidenceColor = (confidence) => {
  if (confidence >= 0.8) return "bg-[#DCFCE7] text-[#166534]";
  if (confidence >= 0.5) return "bg-[#FEF3C7] text-[#92400E]";
  return "bg-[#FEE2E2] text-[#991B1B]";
};

const getFallbackValue = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

/**
 * ProductItem — single grocery product card rendered inside a category
 * group. Pure presentational; all side-effects are delegated to callbacks:
 *   - onPreviewImage({ src, name })
 *   - onToggleHaveIt(productKey, categoryName, productId, price)
 *   - onToggleChecked(productKey)
 *   - onRequestReplace({ ingredientName, currentProductId })  [admin]
 *   - onRequestSizeAdjust({ productId, productName, currentSizes })  [admin]
 *
 * calculateDisplayDetails must be a memoized/pure function passed from parent.
 */
const ProductItem = ({
  item,
  categoryName = null,
  isAdmin = false,
  shoppingMode = false,
  hasIt,
  isChecked,
  calculateDisplayDetails,
  onPreviewImage,
  onToggleHaveIt,
  onToggleChecked,
  onRequestReplace,
  onRequestSizeAdjust,
}) => {
  const { t, i18n } = useTranslation("mealplan");

  const productName = getLocalizedProductName(item, i18n.language);
  const productKey = item.productName || productName;
  const searchName = getFallbackValue(item.originalName, item.name);
  const { displayStr, toBuyStr, isAI, isError } = calculateDisplayDetails(item);
  const productImage = getProductImage(item);
  const productPrice =
    parseFloat((item.totalPrice || "").toString().replace(/[^0-9.-]/g, "")) ||
    null;
  const productId = item.productId || null;

  const matchConfidence = item.matchConfidence;
  const aiReasoning = item.aiReasoning;
  const hasAIMatch =
    matchConfidence !== undefined &&
    matchConfidence !== null &&
    matchConfidence < 1.0;

  return (
    <div
      className={`bg-[#fff] p-4 border border-b-[var(--Color-Grays-Gray-3,#E8E6E3)] border-[0.5px] ${
        isAI ? "bg-[#FFF9E6]" : isError ? "bg-[#FEF2F2]" : ""
      } ${shoppingMode && isChecked ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
          onClick={() =>
            onPreviewImage &&
            onPreviewImage({ src: productImage, name: productName })
          }
        >
          <img
            src={productImage}
            alt={productName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "/assets/products/no-image.jpg";
            }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-0">
            <h4 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[24px] flex-1">
              {productName}
              {isAdmin && (
                <span className="text-[var(--Color-Grays-Gray-7,#999999)] text-[10px] font-medium leading-[15px] mb-2">
                  &nbsp;&nbsp;( {searchName} )
                </span>
              )}
            </h4>
            {isAI && (
              <span className="text-[10px] bg-[#FFCB00] text-[#0D0C07] px-2 py-1 rounded-[4px] font-medium ml-2">
                {t("grocery.estimated", "Estimated")}
              </span>
            )}
            {isError && (
              <span className="text-[10px] bg-[#EF4444] text-white px-2 py-1 rounded-[4px] font-medium ml-2">
                {t("grocery.issue", "Issue")}
              </span>
            )}
          </div>
          <p className="text-[var(--Color-Grays-Gray-7,#353227)] text-[12px] font-medium leading-[20px] mb-2">
            {displayStr}
          </p>
          <p
            className={`inline text-[var(--Color-Grays-Gray-7,#353227)] text-[12px] leading-[20px] px-2 py-1.5 rounded-[8px] ${
              isError
                ? "bg-[#F3F3F2]"
                : hasIt && !shoppingMode
                ? "line-through border-[0.5px] border-[var(--Color-Grays-Gray-0,#FFF)] bg-[rgba(0,153,0,0.06)]"
                : "bg-[var(--Color-Grays-Gray-2,#F5F3F0)]"
            }`}
          >
            {toBuyStr}
          </p>
          {!shoppingMode && (
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() =>
                  onToggleHaveIt &&
                  onToggleHaveIt(productKey, categoryName, productId, productPrice)
                }
                className="flex text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[12.4px] font-semibold leading-[20px] border-b-[1px] md:hover:text-[#4D4B42] border-[#FFCB00]"
                disabled={isError}
              >
                {isError
                  ? t("grocery.notAvailable")
                  : hasIt
                  ? t("grocery.dontHaveIt")
                  : t("grocery.haveIt")}
              </button>
              {isAdmin && (
                <button
                  onClick={() =>
                    onRequestReplace &&
                    onRequestReplace({
                      ingredientName: searchName,
                      currentProductId: productId,
                    })
                  }
                  className="flex text-[var(--Color-Grays-Gray-6,#65635D)] text-[12.4px] font-medium leading-[20px] border-b-[1px] md:hover:text-[#4D4B42] border-[#A09F9C]"
                  disabled={isError}
                  title={t("grocery.admin.replaceProductTooltip", "Choose a different product")}
                >
                  {t("grocery.admin.replaceProduct", "Replace")}
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() =>
                    onRequestSizeAdjust &&
                    onRequestSizeAdjust({
                      productId: productId,
                      productName: productName,
                      currentSizes: item.product?.sizes || [item.selectedSize],
                    })
                  }
                  className="flex text-[var(--Color-Grays-Gray-6,#65635D)] text-[12.4px] font-medium leading-[20px] border-b-[1px] md:hover:text-[#4D4B42] border-[#A09F9C]"
                  disabled={isError}
                  title={t("grocery.admin.adjustSizesTooltip", "Edit product sizes")}
                >
                  {t("grocery.admin.adjustSizes", "Adjust size")}
                </button>
              )}
            </div>
          )}
        </div>

        {hasAIMatch && (
          <div className="flex items-top gap-2 mb-1.5">
            <span
              className={`text-[10px] px-2 py-0.5 rounded-[4px] flex-start font-medium ${getConfidenceColor(
                matchConfidence
              )}`}
            >
              {t("grocery.matchConfidence", {
                percentage: Math.round(matchConfidence * 100),
                defaultValue: "{{percentage}}% match",
              })}
            </span>
            {aiReasoning && (
              <span
                className="text-[10px] text-[var(--Color-Grays-Gray-6,#65635D)] italic max-w-[200px]"
                title={aiReasoning}
              >
                {aiReasoning}
              </span>
            )}
          </div>
        )}

        {shoppingMode && (
          <button
            onClick={() => onToggleChecked && onToggleChecked(productKey)}
            disabled={isError}
            className={`flex-shrink-0 w-8 h-8 rounded-[12px] border-[0.5px] border-[#56554D] flex items-center justify-center transition-all ${
              isChecked
                ? "bg-[#FFCB00] border-[#0D0C07]"
                : "border-[#56554D] bg-white md:hover:border-[#0D0C07]"
            } ${isError ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
          >
            {isChecked ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M16.6668 5L7.50016 14.1667L3.3335 10"
                  stroke="#0D0C07"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M16.6668 5L7.50016 14.1667L3.3335 10"
                  stroke="#EBEBEA"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProductItem);
